export const lengthRule =
  (length, message = null) =>
  (v) => {
    if (!v) return true;
    if (v.length === length) return true;
    return message || `Must be ${length} characters`;
  };

export const lengthRangeRule =
  (min, max, message = null) =>
  (v) => {
    if (!v) return true;
    if (v.length >= min && v.length <= max) return true;
    return message || `Must be between ${min} and ${max} characters`;
  };

export const requiredRule = (v) => {
  return !v || v?.length === 0 ? "This field is required" : true;
};

export const valueInItemsAutocompleteRule = (v, errorMessage, { itemsRef } = {}) => {
  if (!v) return true;
  return itemsRef?.some((item) => item.value === v) ? true : errorMessage;
};

export const isIntegerRule = (v) => {
  if (!v) return true;
  if (isNaN(v)) return "Must be a number";
  if (!Number.isInteger(Number(v))) return "Must be an integer";
  return true;
};

export const isNumericRule = (v) => {
  if (!v) return true;
  if (isNaN(v)) return "Must be a number";
  return true;
};

export const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;

  if (obj1 == null || obj2 == null) return false;

  if (typeof obj1 !== typeof obj2) return false;

  if (typeof obj1 === "string" && typeof obj2 === "string") {
    return obj1.toLowerCase() === obj2.toLowerCase();
  }

  if (typeof obj1 !== "object") return obj1 === obj2;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
};

export const valueInItemsRule = (v, items) => {
  if (!v) return true;

  const isValueInItems = items.some((item) => {
    const itemValue = item.value ?? item;
    if (typeof itemValue === "object" && typeof v === "object") {
      return deepEqual(itemValue, v);
    }
    if (typeof itemValue === "string" && typeof v === "string") {
      return itemValue.toLowerCase() === v.toLowerCase();
    }
    return itemValue === v;
  });

  if (isValueInItems) {
    return true;
  }
  let options = items.map((item) => item.title ?? item).join(", ");
  if (options.length > 200) {
    options = options.substring(0, 200) + "...";
  }
  return "Invalid value, must be one of the following: " + options;
};

export const zipCodeFormatRule = (v) => {
  if (!v) return true;
  return v.match(/^\d{5}(-\d{4})?$/) ? true : "Invalid zip code";
};

/** Exactly 5 digits — for clients that only use 5-digit ZIP (e.g. Brooks Infusion). */
export const zipCode5DigitRule = (v) => {
  if (!v) return true;
  return v.match(/^\d{5}$/) ? true : "ZIP must be exactly 5 digits";
};

const synonymMap = {
  grams: ["gm", "g", "gram"],
  kilograms: ["kg", "kgs", "kilogram"],
  pounds: ["lbs", "lb", "pound"],
  inches: ["in", "inch"],
  centimeters: ["cm", "centimeter"],
  male: ["m", "man"],
  female: ["f", "woman"],
  "non-binary": ["nb", "nonbinary", "non binary"],
  undisclosed: ["unknown", "unspecified", "u", "not specified"],
  primary: ["pri", "prim"],
  secondary: ["sec", "second"],
};

export const findDropdownMatch = (currentValue, items) => {
  if (
    !currentValue ||
    !items?.length ||
    valueInItemsRule(currentValue, items) === true ||
    typeof currentValue !== "string"
  )
    return null;

  // Strategy 1: Option contains current value
  let match = items.find((item) => {
    const optionText = typeof item === "string" ? item : item.title || "";
    return typeof optionText === "string" && optionText.toLowerCase().includes(currentValue.toLowerCase());
  });

  // Strategy 2: Current value contains option
  if (!match) {
    match = items.find((item) => {
      const optionText = typeof item === "string" ? item : item.title || "";
      return typeof optionText === "string" && currentValue.toLowerCase().includes(optionText.toLowerCase());
    });
  }

  // Strategy 3: Synonym matching
  if (!match) {
    const currentValueLower = currentValue.toLowerCase();
    match = items.find((item) => {
      const optionText = typeof item === "string" ? item : item.title || "";
      const optionTextLower = optionText.toLowerCase();

      const synonyms = synonymMap[currentValueLower] || [];
      const optionSynonyms = synonymMap[optionTextLower] || [];

      return (
        synonyms.includes(optionTextLower) ||
        optionSynonyms.includes(currentValueLower) ||
        (synonyms.length > 0 && optionSynonyms.length > 0 && synonyms.some((s) => optionSynonyms.includes(s)))
      );
    });
  }

  return match ? (typeof match === "string" ? match : match.value) : null;
};

export const fixFieldIfNeeded = (currentValue, items, onUpdate) => {
  const correctedValue = findDropdownMatch(currentValue, items);
  if (correctedValue) {
    onUpdate(correctedValue);
  }
};

export const fixDropdownValidationErrors = (fields, valueObject, emitFieldUpdate) => {
  const dropdownFields = fields.filter(
    (field) => field.cmp?.component === "v-select" || field.cmp?.component === "v-combobox"
  );

  dropdownFields.forEach((field) => {
    const items = field.cmp?.vBind?.items || field.items || [];
    fixFieldIfNeeded(valueObject[field.key], items, (value) => emitFieldUpdate(value, field));
  });
};

// Max length rule - generic, reusable
export const maxLengthRule = (maxLength) => (v) => {
  if (!v) return true;
  if (v.length > maxLength) return `Must be ${maxLength} characters or less`;
  return true;
};

// Name validation: permissive - allows Unicode letters (international names),
// spaces, apostrophes, hyphens, periods
// Examples that pass: "José", "O'Brien", "Mary-Jane", "François", "李明"
export const nameRule =
  (maxLength = 50) =>
  (v) => {
    if (!v) return true;
    if (v.length > maxLength) return `Must be ${maxLength} characters or less`;
    // Block obviously invalid chars (digits, @, $, etc.) but allow Unicode letters
    if (/[0-9@$%^&*()=+\[\]{}|\\<>]/.test(v)) return "Contains invalid characters";
    return true;
  };

// City validation: same as name - allows international city names
// Examples that pass: "São Paulo", "Zürich", "東京"
export const cityRule =
  (maxLength = 50) =>
  (v) => {
    if (!v) return true;
    if (v.length > maxLength) return `Must be ${maxLength} characters or less`;
    // Block obviously invalid chars but allow Unicode letters
    if (/[0-9@$%^&*()=+\[\]{}|\\<>]/.test(v)) return "Contains invalid characters";
    return true;
  };

// Address validation: very permissive - allows most chars except obvious invalid ones
// Examples that pass: "123 Main St.", "Av. Paulista, 1000", "台北市中正區"
export const addressRule =
  (maxLength = 100) =>
  (v) => {
    if (!v) return true;
    if (v.length > maxLength) return `Must be ${maxLength} characters or less`;
    return true;
  };

// Phone validation: permissive - just checks for reasonable length and digits
// Allows international formats: +1 (555) 123-4567, +44 20 7946 0958, etc.
export const phoneRule = (v) => {
  if (!v) return true;
  const cleaned = v.replace(/\D/g, "");
  // Allow 7-15 digits (covers domestic and international)
  if (cleaned.length < 7 || cleaned.length > 15) return "Phone number must be 7-15 digits";
  return true;
};

// US phone validation: exactly 10 digits (accepts leading country code 1)
export const usPhoneRule = (v) => {
  if (!v) return true;
  let digits = v.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length !== 10) return "Phone number must contain 10 digits";
  return true;
};

export const emailRule = (v) => {
  if (!v) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? true : "Invalid email address";
};

export const minLengthRule = (min) => (v) => {
  if (!v) return true;
  if (v.trim().length < min) return `Must be at least ${min} characters`;
  return true;
};
